import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Default } from "../../components/default/Default";
import PostRequest from "../../services/PostRequest";

const PAGE_SIZE = 50;

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "name", label: "Name" },
];

const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

// One category node + its children (2 levels deep is plenty for the sidebar).
function CategoryNode({ node, activeSlug, onPick, depth }) {
  const active = node.slug === activeSlug;
  return (
    <div>
      <button
        type="button"
        onClick={() => onPick(node.slug)}
        style={{
          display: "flex", justifyContent: "space-between", width: "100%",
          padding: "6px 8px", paddingLeft: 8 + depth * 14, border: "none",
          borderRadius: 6, cursor: "pointer", textAlign: "left",
          background: active ? "var(--mca-primary)" : "transparent",
          color: active ? "#fff" : "var(--mca-text)",
          fontSize: 13, fontWeight: active ? 600 : 500,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {node.name}
        </span>
        <span style={{ opacity: 0.7, marginLeft: 8 }}>{node.product_count}</span>
      </button>
      {node.children && node.children.map((c) => (
        <CategoryNode key={c.slug} node={c} activeSlug={activeSlug} onPick={onPick} depth={depth + 1} />
      ))}
    </div>
  );
}

export const CatalogScanner = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [categorySlug, setCategorySlug] = useState("");
  const [brand, setBrand] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [inStock, setInStock] = useState(false);
  const [excludeBulky, setExcludeBulky] = useState(false);
  const [sort, setSort] = useState("newest");

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createPostcode, setCreatePostcode] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const buildFilter = useCallback(() => {
    const f = { sort };
    if (categorySlug) f.category_slug = categorySlug;
    if (brand.trim()) f.brand = [brand.trim()];
    if (priceMin !== "") f.price_min_cents = Math.round(Number(priceMin) * 100);
    if (priceMax !== "") f.price_max_cents = Math.round(Number(priceMax) * 100);
    if (inStock) f.in_stock = true;
    if (excludeBulky) f.big_and_bulky = "exclude";
    return f;
  }, [categorySlug, brand, priceMin, priceMax, inStock, excludeBulky, sort]);

  useEffect(() => {
    (async () => {
      try {
        const data = await PostRequest("/schedules/repco/categories/", {});
        setCategories(data.categories || []);
      } catch (e) {
        setError(e.message || "Failed to load categories");
      }
    })();
  }, []);

  const loadProducts = useCallback(async (targetPage) => {
    setLoading(true);
    setError("");
    try {
      const data = await PostRequest("/schedules/repco/categories/products/", {
        ...buildFilter(), page: targetPage, page_size: PAGE_SIZE,
      });
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setPage(data.page || targetPage);
    } catch (e) {
      setError(e.message || "Failed to load products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilter]);

  useEffect(() => {
    loadProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => loadProducts(1);
  const pickCategory = (slug) => {
    setCategorySlug(slug === categorySlug ? "" : slug);
  };
  // reload when category toggles (immediate)
  useEffect(() => {
    loadProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug]);

  const submitCreate = async () => {
    setCreateError("");
    if (!createName.trim()) { setCreateError("Give the collection a name."); return; }
    if (!createPostcode.trim()) { setCreateError("Enter a Repco postcode you've added."); return; }
    setCreating(true);
    try {
      const res = await PostRequest("/schedules/repco/categories/create_collection/", {
        ...buildFilter(), name: createName.trim(), customer_postcode: createPostcode.trim(),
      });
      setShowCreate(false);
      navigate(`/collections/${res.collection_id}/records`);
    } catch (e) {
      setCreateError(e.message || "Failed to create collection");
    } finally {
      setCreating(false);
    }
  };

  const inputStyle = {
    padding: "8px 10px", border: "1px solid var(--mca-stroke)",
    background: "var(--mca-surface)", color: "var(--mca-text)",
    borderRadius: 8, fontSize: 13, width: "100%",
  };

  return (
    <Default>
      <div className="mca-content">
        <div className="col-head">
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.02em" }}>
              Repco Catalog
            </h1>
            <p style={{ color: "var(--mca-muted)", fontSize: "14px", margin: "6px 0 0" }}>
              Browse the Repco catalog and turn a filter into a live scrape collection.
              Prices &amp; freight shown are the reference values captured at postcode 4500 —
              your collection re-scrapes each product live at your own postcode.
            </p>
          </div>
          <div className="mca-page-actions">
            <button className="mca-btn mca-btn--primary" type="button" onClick={() => setShowCreate(true)}>
              <span className="mca-btn__label">Create collection from filter →</span>
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* Category sidebar */}
          <aside style={{
            flex: "0 0 240px", background: "var(--mca-surface)",
            border: "1px solid var(--mca-stroke)", borderRadius: 12, padding: 12,
            maxHeight: "70vh", overflowY: "auto",
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--mca-muted)", textTransform: "uppercase", marginBottom: 8 }}>
              Categories
            </div>
            <button
              type="button"
              onClick={() => setCategorySlug("")}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "6px 8px",
                border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13,
                fontWeight: categorySlug === "" ? 600 : 500,
                background: categorySlug === "" ? "var(--mca-primary)" : "transparent",
                color: categorySlug === "" ? "#fff" : "var(--mca-text)",
              }}
            >
              All products
            </button>
            {categories.map((c) => (
              <CategoryNode key={c.slug} node={c} activeSlug={categorySlug} onPick={pickCategory} depth={0} />
            ))}
          </aside>

          {/* Main: filter bar + results */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Filter bar */}
            <div style={{
              display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
              background: "var(--mca-surface)", border: "1px solid var(--mca-stroke)",
              borderRadius: 12, padding: 12, marginBottom: 12,
            }}>
              <label style={{ flex: "1 1 140px" }}>
                <div style={{ fontSize: 11, color: "var(--mca-muted)", marginBottom: 4 }}>Brand</div>
                <input style={inputStyle} value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Monroe" />
              </label>
              <label style={{ flex: "0 0 100px" }}>
                <div style={{ fontSize: 11, color: "var(--mca-muted)", marginBottom: 4 }}>Min $</div>
                <input style={inputStyle} type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} />
              </label>
              <label style={{ flex: "0 0 100px" }}>
                <div style={{ fontSize: 11, color: "var(--mca-muted)", marginBottom: 4 }}>Max $</div>
                <input style={inputStyle} type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} />
              </label>
              <label style={{ flex: "0 0 170px" }}>
                <div style={{ fontSize: 11, color: "var(--mca-muted)", marginBottom: 4 }}>Sort</div>
                <select style={inputStyle} value={sort} onChange={(e) => setSort(e.target.value)}>
                  {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, paddingBottom: 8 }}>
                <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} /> In stock
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, paddingBottom: 8 }}>
                <input type="checkbox" checked={excludeBulky} onChange={(e) => setExcludeBulky(e.target.checked)} /> Exclude bulky
              </label>
              <button className="mca-btn mca-btn--primary" type="button" onClick={applyFilters} style={{ marginBottom: 2 }}>
                <span className="mca-btn__label">Apply</span>
              </button>
            </div>

            {error && (
              <div style={{ padding: 12, background: "#fdecec", color: "#a12", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div style={{ fontSize: 13, color: "var(--mca-muted)", marginBottom: 8 }}>
              {loading ? "Loading…" : `${total.toLocaleString()} products`}
            </div>

            {/* Results table */}
            <div style={{ overflowX: "auto", background: "var(--mca-surface)", border: "1px solid var(--mca-stroke)", borderRadius: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--mca-muted)", borderBottom: "1px solid var(--mca-stroke)" }}>
                    <th style={{ padding: "10px 12px" }}></th>
                    <th style={{ padding: "10px 12px" }}>Product</th>
                    <th style={{ padding: "10px 12px" }}>Brand</th>
                    <th style={{ padding: "10px 12px" }}>Price</th>
                    <th style={{ padding: "10px 12px" }}>Member</th>
                    <th style={{ padding: "10px 12px" }} title="Reference freight captured at postcode 4500">Freight @4500</th>
                    <th style={{ padding: "10px 12px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.product_code} style={{ borderBottom: "1px solid var(--mca-stroke)" }}>
                      <td style={{ padding: "8px 12px" }}>
                        {p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} /> : null}
                      </td>
                      <td style={{ padding: "8px 12px", maxWidth: 340 }}>
                        <a href={p.url} target="_blank" rel="noreferrer" style={{ color: "var(--mca-text)", fontWeight: 600, textDecoration: "none" }}>
                          {p.title}
                        </a>
                        <div style={{ color: "var(--mca-muted)", fontSize: 12 }}>{p.product_code} · {p.category_path}</div>
                      </td>
                      <td style={{ padding: "8px 12px" }}>{p.brand || "—"}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{money(p.price_value)}</td>
                      <td style={{ padding: "8px 12px", color: "var(--mca-muted)" }}>{money(p.auto_club_price_value)}</td>
                      <td style={{ padding: "8px 12px" }}>{p.big_and_bulky ? "Bulky" : money(p.freight_value)}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, background: p.in_stock ? "#e7f6ec" : "#f4f4f4", color: p.in_stock ? "#1a7a3e" : "#888" }}>
                          {p.in_stock ? "In stock" : "Out"}
                        </span>
                        {p.in_store_only && <span style={{ marginLeft: 6, fontSize: 12, color: "#b8860b" }}>In-store only</span>}
                      </td>
                    </tr>
                  ))}
                  {!loading && products.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "var(--mca-muted)" }}>No products match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", marginTop: 14 }}>
                <button className="mca-btn" type="button" disabled={page <= 1} onClick={() => loadProducts(page - 1)}>Prev</button>
                <span style={{ fontSize: 13, color: "var(--mca-muted)" }}>Page {page} of {totalPages}</span>
                <button className="mca-btn" type="button" disabled={page >= totalPages} onClick={() => loadProducts(page + 1)}>Next</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create-from-filter dialog */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
             onClick={() => !creating && setShowCreate(false)}>
          <div style={{ background: "var(--mca-surface)", border: "1px solid var(--mca-stroke)", borderRadius: 14, padding: 24, width: 440, maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Create collection from filter</h2>
            <p style={{ color: "var(--mca-muted)", fontSize: 13, margin: "0 0 16px" }}>
              The {total.toLocaleString()} matched products (up to 5,000) will be scraped live at your postcode.
            </p>
            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "var(--mca-muted)", marginBottom: 4 }}>Collection name</div>
              <input style={inputStyle} value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="e.g. Monroe shocks — Brisbane" />
            </label>
            <label style={{ display: "block", marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "var(--mca-muted)", marginBottom: 4 }}>Customer postcode</div>
              <input style={inputStyle} value={createPostcode} onChange={(e) => setCreatePostcode(e.target.value)} placeholder="e.g. 4500" />
            </label>
            <p style={{ color: "var(--mca-muted)", fontSize: 12, margin: "0 0 16px" }}>
              Must be a Repco postcode you've already added on the Postcodes page.
            </p>
            {createError && <div style={{ padding: 10, background: "#fdecec", color: "#a12", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{createError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="mca-btn" type="button" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</button>
              <button className="mca-btn mca-btn--primary" type="button" onClick={submitCreate} disabled={creating}>
                <span className="mca-btn__label">{creating ? "Creating…" : "Create & run"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Default>
  );
};
